import { DateRange, MetricEventTypes, ScoreFieldTypes, ScoreTypes, TimePeriods } from "@Enums/index";
import { normalizeBybIq, normalizeMetric, normalizeMetrics } from "@Helpers/metric-normalizer";
import {
  LongTermMetricResults,
  LongTermResultPoint,
  RadarMetricInfo,
} from "@Helpers/types";
import type { UserStats } from "@Shared/schemas/stats/statsSchemas";
import {
  computeScanScore,
  computeScanScoreForBybIq,
  MAX_SCAN_RATE_FOR_BYB_IQ,
} from "@Shared/utilities";
import dayjs from "dayjs";
import { TFunction } from "i18next";

export const toNumber = (value?: number): number => value ?? 0;

export const statsConfig = [
  {
    score_name: ScoreFieldTypes.AWARENESS_SCORE,
    score_field: ScoreFieldTypes.AWARENESS_SCORE,
    score_type: ScoreTypes.AVERAGE,
  },
  {
    score_name: ScoreFieldTypes.SCAN_SCORE,
    score_field: ScoreFieldTypes.SCAN_SCORE,
    score_type: ScoreTypes.AVERAGE,
  },
  {
    score_name: ScoreFieldTypes.SCAN_TIMING,
    score_field: ScoreFieldTypes.SCAN_TIMING,
    score_type: ScoreTypes.AVERAGE,
  },
  {
    score_name: ScoreFieldTypes.PASSES_SCORE,
    score_field: ScoreFieldTypes.PASSES_SCORE,
    score_type: ScoreTypes.AVERAGE,
  },
  {
    score_name: ScoreFieldTypes.RECEIVALS_MADE,
    score_field: ScoreFieldTypes.RECEIVALS_MADE,
    score_type: ScoreTypes.AVERAGE,
  },
  {
    score_name: ScoreFieldTypes.CRITICAL_SCANS,
    score_field: ScoreFieldTypes.CRITICAL_SCANS,
    score_type: ScoreTypes.AVERAGE,
  },
  {
    score_name: ScoreFieldTypes.TOTAL_SCORE,
    score_field: ScoreFieldTypes.TOTAL_SCORE,
    score_type: ScoreTypes.AVERAGE,
  },
  {
    score_name: ScoreFieldTypes.BYB_IQ,
    score_field: ScoreFieldTypes.BYB_IQ,
    score_type: ScoreTypes.AVERAGE,
  },
];

export const getFromDate = (dateRange: DateRange) => {
  let from;
  switch (dateRange) {
    case DateRange.ALL_TIME:
      from = dayjs("1970-01-01").startOf("day").toDate();
      break;
    case DateRange.PAST_YEAR:
      from = dayjs().startOf("hour").subtract(1, "year").toDate();
      break;
    case DateRange.PAST_3_MONTHS:
      from = dayjs().startOf("hour").subtract(3, "month").toDate();
      break;
    case DateRange.PAST_MONTH:
      from = dayjs().startOf("hour").subtract(1, "month").toDate();
      break;
    case DateRange.PAST_14_DAYS:
      from = dayjs().startOf("hour").subtract(14, "day").toDate();
      break;
  }
  return from;
};

export const filterLongTermResultsByDateRange = (results: LongTermResultPoint[], dateRange: DateRange): LongTermResultPoint[] => {
  const startDate = dayjs(getFromDate(dateRange)).startOf("day");

  return results.filter(result => {
    const resultDate = dayjs(result.date);

    return resultDate.isAfter(startDate) || resultDate.isSame(startDate);
  });
};

export const filterLongTermMetricResultsByDateRange = (metricResults: LongTermMetricResults, dateRange: DateRange): LongTermMetricResults => {
  return Object.keys(metricResults).reduce((acc, metricKey) => {
    const metricType = metricKey as MetricEventTypes;

    acc[metricType] = filterLongTermResultsByDateRange(metricResults[metricType], dateRange);

    return acc;
  }, {} as LongTermMetricResults);
};

export const getLatestDefinedScore = <T extends { score?: number | null }>(scores: T[]): number | null => {
  return [...scores].reverse().find(item => item.score !== null && item.score !== undefined)?.score ?? null;
};

export const calculateHeadlineBybIq = <T extends { byb_iq: number | null }>(items: T[]): number => {
  const latestItems = items.slice(0, 5);
  const bybIq = latestItems.length > 0 ? latestItems.reduce((acc, item) => acc + (item.byb_iq ?? 0), 0) / latestItems.length : 0;

  return normalizeBybIq(bybIq).displayValue;
};

export const parseRadarStats = (current?: UserStats | null, previous?: UserStats | null): RadarMetricInfo[] => {
  const currentMetrics = current ? normalizeMetrics(current) : null;
  const previousMetrics = previous ? normalizeMetrics(previous) : null;

  return [
    {
      name: MetricEventTypes.AWARENESS,
      current: currentMetrics ? currentMetrics[MetricEventTypes.AWARENESS].radarValue : 0,
      previous: previousMetrics ? previousMetrics[MetricEventTypes.AWARENESS].radarValue : 0,
      fullMark: 100,
      domain: [0, 100],
    },
    {
      name: MetricEventTypes.SCAN_FREQUENCY,
      current: currentMetrics ? currentMetrics[MetricEventTypes.SCAN_FREQUENCY].radarValue : 0,
      previous: previousMetrics ? previousMetrics[MetricEventTypes.SCAN_FREQUENCY].radarValue : 0,
      fullMark: 100,
      domain: [0, 100],
    },
    {
      name: MetricEventTypes.SCAN_TIMING,
      current: currentMetrics ? currentMetrics[MetricEventTypes.SCAN_TIMING].radarValue : 0,
      previous: previousMetrics ? previousMetrics[MetricEventTypes.SCAN_TIMING].radarValue : 0,
      fullMark: 100,
      domain: [0, 100],
    },
    {
      name: MetricEventTypes.DECISION,
      current: currentMetrics ? currentMetrics[MetricEventTypes.DECISION].radarValue : 0,
      previous: previousMetrics ? previousMetrics[MetricEventTypes.DECISION].radarValue : 0,
      fullMark: 100,
      domain: [0, 100],
    },
    {
      name: MetricEventTypes.CRITICAL_SCAN,
      current: currentMetrics ? currentMetrics[MetricEventTypes.CRITICAL_SCAN].radarValue : 0,
      previous: previousMetrics ? previousMetrics[MetricEventTypes.CRITICAL_SCAN].radarValue : 0,
      fullMark: 100,
      domain: [0, 100],
    },
  ];
};

interface ProgressDataPoint {
  name: string;
  score: number | null;
  metricScore: number | null;
  date: string;
}

interface GroupedStatMetadata {
  name: string;
  date: string | null;
}

const getGroupedStatMetadata = (stat: UserStats, groupBy: TimePeriods, i18n: TFunction): GroupedStatMetadata => {
  switch (groupBy) {
    case TimePeriods.MONTH:
      return {
        name: i18n(`month.number.${stat.month}`),
        date: dayjs(`${stat.year}-${String(stat.month).padStart(2, "0")}-01`).toISOString(),
      };
    case TimePeriods.WEEK:
      return {
        name: `${i18n("week-short")}${stat.week}`,
        date: dayjs(`${stat.year}-01-01`)
          .add(((stat.week || 1) - 1) * 7, "day")
          .toISOString(),
      };
    case TimePeriods.DATE: {
      const year = stat.year && stat.year > 2000 ? stat.year : dayjs().year();

      return {
        name: `${stat.day} ${i18n(`month.number.${stat.month}`)}`,
        date: dayjs(`${year}-${String(stat.month).padStart(2, "0")}-${String(stat.day).padStart(2, "0")}`).toISOString(),
      };
    }
    default:
      return {
        name: "",
        date: null,
      };
  }
};

// @davidrocha: TODO: move this to the backend once we finish the development setup
const fillMissingDays = (results: ProgressDataPoint[], dateRange?: DateRange): ProgressDataPoint[] => {
  if (results.length === 0) return results;

  const filledResults: ProgressDataPoint[] = [];

  const sortedResults = [...results].sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf());

  const startDate = dayjs(sortedResults[0].date);
  const endDate = dayjs().endOf("day");

  const existingData = new Map<string, ProgressDataPoint>();
  sortedResults.forEach(result => {
    const dateKey = dayjs(result.date).format("YYYY-MM-DD");
    existingData.set(dateKey, result);
  });

  const shouldShowLabel = (date: dayjs.Dayjs): boolean => {
    if (!dateRange) return date.date() === 1;

    const month = date.month();

    switch (dateRange) {
      case DateRange.ALL_TIME:
        return date.month() === 0 && date.date() === 1;
      case DateRange.PAST_YEAR:
        return month % 3 === 0 && date.date() === 1;
      case DateRange.PAST_3_MONTHS:
        return date.date() === 1;
      case DateRange.PAST_MONTH:
      case DateRange.PAST_14_DAYS:
        return date.day() === 1 || date.isSame(startDate, "day");
      default:
        return date.date() === 1;
    }
  };

  const getLabelText = (date: dayjs.Dayjs): string => {
    switch (dateRange) {
      case DateRange.ALL_TIME:
        return date.format("YYYY");
      case DateRange.PAST_YEAR:
      case DateRange.PAST_3_MONTHS:
        return date.format("MMM");
      case DateRange.PAST_MONTH:
      case DateRange.PAST_14_DAYS:
        return `${date.date()} ${date.format("MMM")}`;
      default:
        return date.date() === 1 ? date.format("MMM") : "";
    }
  };

  let currentDate = startDate;
  while (currentDate.isBefore(endDate) || currentDate.isSame(endDate)) {
    const dateKey = currentDate.format("YYYY-MM-DD");

    if (existingData.has(dateKey)) {
      const existingResult = existingData.get(dateKey);
      if (existingResult) {
        const updatedResult: ProgressDataPoint = {
          ...existingResult,
          name: shouldShowLabel(currentDate) ? getLabelText(currentDate) : "",
        };
        filledResults.push(updatedResult);
      }
    } else {
      filledResults.push({
        name: shouldShowLabel(currentDate) ? getLabelText(currentDate) : "",
        score: null,
        metricScore: null,
        date: currentDate.toISOString(),
      });
    }

    currentDate = currentDate.add(1, "day");
  }

  return filledResults;
};

export const calculateBybIqLongTermResults = (
  stats: UserStats[],
  groupBy: TimePeriods,
  i18n: TFunction,
  dateRange?: DateRange
): LongTermResultPoint[] => {
  const results: ProgressDataPoint[] = stats
    .filter(stat => stat.byb_iq != null)
    .map(stat => {
      const groupedStatMetadata = getGroupedStatMetadata(stat, groupBy, i18n);
      const normalizedBybIq = normalizeBybIq(stat.byb_iq);

      return {
        name: groupedStatMetadata.name,
        score: normalizedBybIq.displayValue,
        metricScore: normalizedBybIq.displayValue,
        unit: normalizedBybIq.unitKey,
        date: groupedStatMetadata.date || "",
      };
    });

  return fillMissingDays(results, dateRange).map(result => ({
    name: result.name,
    score: result.score,
    unit: "metric.byb_iq.long",
    date: result.date,
  }));
};

const hasLongTermMetricValue = (stat: UserStats, metricType: MetricEventTypes): boolean => {
  switch (metricType) {
    case MetricEventTypes.SCAN_FREQUENCY:
      return stat.scan_score != null;
    case MetricEventTypes.SCAN_TIMING:
      return stat.scan_timing != null;
    case MetricEventTypes.AWARENESS:
      return stat.awareness_score != null;
    case MetricEventTypes.DECISION:
      return stat.passes_score != null;
    case MetricEventTypes.CRITICAL_SCAN:
      return stat.critical_scans != null && stat.receivals_made != null;
    default:
      return false;
  }
};

export const calculateMainMetricsLongTermResults = (
  stats: UserStats[],
  groupBy: TimePeriods,
  i18n: TFunction,
  dateRange?: DateRange
): LongTermMetricResults => {
  const metricGroups: LongTermMetricResults = {
    [MetricEventTypes.SCAN_FREQUENCY]: [] as LongTermResultPoint[],
    [MetricEventTypes.SCAN_TIMING]: [] as LongTermResultPoint[],
    [MetricEventTypes.AWARENESS]: [] as LongTermResultPoint[],
    [MetricEventTypes.CRITICAL_SCAN]: [] as LongTermResultPoint[],
    [MetricEventTypes.DECISION]: [] as LongTermResultPoint[],
  };

  stats.forEach(stat => {
    const groupedStatMetadata = getGroupedStatMetadata(stat, groupBy, i18n);

    Object.keys(metricGroups).forEach(key => {
      const metricType = key as MetricEventTypes;
      const metric = normalizeMetric(stat, metricType);
      const hasMetricValue = hasLongTermMetricValue(stat, metricType);

      metricGroups[metricType].push({
        name: groupedStatMetadata.name,
        score: hasMetricValue ? metric.displayValue : null,
        unit: metric.unitKey,
        date: groupedStatMetadata.date || null,
      });
    });
  });

  Object.keys(metricGroups).forEach(key => {
    const metricData = metricGroups[key as MetricEventTypes];

    const progressDataPoints: ProgressDataPoint[] = metricData.map(item => ({
      name: item.name,
      score: item.score,
      metricScore: item.score,
      date: item.date || "",
    }));

    const filledData = fillMissingDays(progressDataPoints, dateRange);

    metricGroups[key as MetricEventTypes] = filledData.map(item => ({
      name: item.name,
      score: item.metricScore ?? null,
      unit: metricData[0]?.unit || "",
      date: item.date,
    }));
  });

  return metricGroups;
};

export const getCriticalScanRatio = (stats: UserStats): number => {
  const receivalsMade = toNumber(stats.receivals_made);

  if (receivalsMade <= 0) {
    return 0;
  }

  return Math.abs(toNumber(stats.critical_scans)) / receivalsMade;
};

const getScanScoreContributionForTotal = (stats: UserStats): number => {
  if (stats.scan_rate != null) {
    return computeScanScoreForBybIq(stats.scan_rate);
  }

  const uncappedScanScore = toNumber(stats.scan_score);
  return Math.min(uncappedScanScore, computeScanScore(MAX_SCAN_RATE_FOR_BYB_IQ));
};

export const computeTotalScore = (stats: UserStats): number => {
  const scoreComponents = [
    ...(stats.awareness_score || stats.awareness_score === 0 ? [stats.awareness_score] : []),
    getScanScoreContributionForTotal(stats),
    toNumber(stats.scan_timing),
    toNumber(stats.passes_score),
    getCriticalScanRatio(stats) * 100,
  ];

  return scoreComponents.reduce((sum, score) => sum + score, 0) / scoreComponents.length;
};

export const getStatsWithDerivedTotalScore = (stats: UserStats): UserStats => {
  return {
    ...stats,
    total_score: computeTotalScore(stats),
  };
};

export const aggregateSessionWindowStats = (userStats: UserStats[]): UserStats | null => {
  if (userStats.length === 0) {
    return null;
  }

  const totals = userStats.reduce(
    (acc, stats) => {
      if (stats.awareness_score || stats.awareness_score === 0) {
        acc.awareness_score_total += stats.awareness_score;
        acc.awareness_score_count += 1;
      }

      acc.scan_score += toNumber(stats.scan_score);
      acc.scan_timing += toNumber(stats.scan_timing);
      acc.passes_score += toNumber(stats.passes_score);
      acc.critical_scans += toNumber(stats.critical_scans);
      acc.receivals_made += toNumber(stats.receivals_made);

      return acc;
    },
    {
      awareness_score_total: 0,
      awareness_score_count: 0,
      scan_score: 0,
      scan_timing: 0,
      passes_score: 0,
      critical_scans: 0,
      receivals_made: 0,
    }
  );

  return getStatsWithDerivedTotalScore({
    id: userStats[0].id,
    user_id: userStats[0].user_id,
    ...(totals.awareness_score_count > 0 ? { awareness_score: totals.awareness_score_total / totals.awareness_score_count } : {}),
    scan_score: totals.scan_score / userStats.length,
    scan_timing: totals.scan_timing / userStats.length,
    passes_score: totals.passes_score / userStats.length,
    critical_scans: totals.critical_scans / userStats.length,
    receivals_made: totals.receivals_made / userStats.length
  });
};
